/** List table: rows, pagination round-trip, per-cell labels. The cover comes from `cover_url` on
 *  the index row only — the index carries no `data`. */
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { AssignmentTemplateListRow } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { getGradeLabel, useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'
import {
  getAssignmentEditPath,
  getAssignmentStatusColor,
  getAssignmentStatusLabel,
  getAssignmentTypeLabel,
  getDisciplineCodeLabel,
} from './assignment-template-options'

interface TableAssignmentTemplatesReturn {
  refTemplates: ComputedRef<AssignmentTemplateListRow[]>
  refIsLoading: ComputedRef<boolean>
  refHasTemplates: ComputedRef<boolean>
  refIsEmpty: ComputedRef<boolean>
  refHasFilters: ComputedRef<boolean>
  refMeta: ComputedRef<Meta>
  refPerPageOptions: ComputedRef<number[]>
  formatDateTime: (value: string | null) => string
  getTypeLabel: (template: AssignmentTemplateListRow) => string
  getDisciplineLabel: (template: AssignmentTemplateListRow) => string
  getGrade: (template: AssignmentTemplateListRow) => string
  getStatusLabel: (template: AssignmentTemplateListRow) => string
  getStatusColor: (template: AssignmentTemplateListRow) => AppBadgeColor
  openTemplate: (template: AssignmentTemplateListRow) => Promise<void>
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableAssignmentTemplates = (): TableAssignmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssignmentTemplatesStore()
  const dayjs = useDayjs()

  const refTemplates = computed<AssignmentTemplateListRow[]>(() => store.getTemplates)
  const refIsLoading = computed<boolean>(() => store.isLoading)
  const refHasTemplates = computed<boolean>(() => store.hasTemplates)
  const refHasFilters = computed<boolean>(() => store.hasFilters)
  const refIsEmpty = computed<boolean>(() => !store.hasTemplates && !store.isLoading)
  const refMeta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  /** The current value is added so a clamped or hand-typed `per_page` never renders a blank select. */
  const refPerPageOptions = computed<number[]>(() => {
    const current = refMeta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getTypeLabel = (template: AssignmentTemplateListRow): string =>
    getAssignmentTypeLabel(template.type)

  const getDisciplineLabel = (template: AssignmentTemplateListRow): string =>
    getDisciplineCodeLabel(template.discipline_code)

  const getGrade = (template: AssignmentTemplateListRow): string => getGradeLabel(template.grade)

  const getStatusLabel = (template: AssignmentTemplateListRow): string =>
    getAssignmentStatusLabel(template.status)

  const getStatusColor = (template: AssignmentTemplateListRow): AppBadgeColor =>
    getAssignmentStatusColor(template.status)

  const openTemplate = async (template: AssignmentTemplateListRow): Promise<void> => {
    await router.push(getAssignmentEditPath(template.id))
  }

  // Nothing writes the response's `current_page` back, so set the store too.
  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useAssignmentTemplatesApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await useAssignmentTemplatesApi()
  }

  return {
    refTemplates,
    refIsLoading,
    refHasTemplates,
    refIsEmpty,
    refHasFilters,
    refMeta,
    refPerPageOptions,
    formatDateTime,
    getTypeLabel,
    getDisciplineLabel,
    getGrade,
    getStatusLabel,
    getStatusColor,
    openTemplate,
    setChangePage,
    setChangePerPage,
  }
}
